import { memo } from "react";
import { Handle, Position, NodeProps } from "reactflow";
import { getImageWithFallback } from "@/utils/imageUtils";
import { Badge } from "@/components/ui/badge";
import { Info } from "lucide-react";

interface DiagramNodeData {
  label: string;
  description?: string;
  image?: string;
  type?: string;
  color?: string;
  isOval?: boolean;
  highlighted?: boolean;
  showImage?: boolean;
  category?: string;
  category_name?: string;
  level?: number; // Cấp độ của node trong cây
  hasDetails?: boolean; // Thêm flag để hiển thị indicator chi tiết
  style?: {
    // Custom style cho node
    width?: number;
    height?: number;
    borderRadius?: number;
    fontSize?: number | string;
    fontWeight?: "normal" | "bold";
    backgroundColor?: string;
    opacity?: number;
    color?: string;
  };
}

const DiagramNode = ({ data, isConnectable }: NodeProps<DiagramNodeData>) => {
  const {
    label,
    description,
    image,
    type,
    color = "#6366F1",
    isOval = false,
    highlighted = false,
    showImage = true,
    category,
    category_name,
    level = 0,
    hasDetails = false, // Mặc định là false
    style = {},
  } = data;

  // Xác định màu nền dựa trên loại node
  const getBgColor = () => {
    // Nếu có custom style với backgroundColor, ưu tiên sử dụng
    if (style?.backgroundColor) return style.backgroundColor;

    // Nếu node được highlight, ưu tiên sử dụng màu nền highlight
    if (highlighted) return "#fecaca"; // Màu đỏ nhạt cho node được highlight

    if (color) return color;

    // Màu dựa theo cấp độ nếu có level
    if (level !== undefined) {
      const levelColors = [
        "#4a90e2", // Cấp 0 - xanh dương
        "#7ed321", // Cấp 1 - xanh lá
        "#bd10e0", // Cấp 2 - tím
        "#f5a623", // Cấp 3 - cam
        "#50e3c2", // Cấp 4 - xanh ngọc
        "#9013fe", // Cấp 5 - tím đậm
        "#8b572a", // Cấp 6+ - nâu
      ];
      return levelColors[Math.min(level, levelColors.length - 1)];
    }

    switch (type) {
      case "root":
        return "#6366F1"; // Indigo
      case "kamavacara":
        return "#3B82F6"; // Blue
      case "rupavacara":
        return "#10B981"; // Green
      case "arupavacara":
        return "#7C3AED"; // Purple
      case "lokuttara":
        return "#EC4899"; // Pink
      case "akusala":
        return "#EF4444"; // Red
      case "ahetuka":
        return "#F59E0B"; // Orange
      case "sobhana":
        return "#06B6D4"; // Cyan
      default:
        return "#6B7280"; // Gray
    }
  };

  const bgColor = getBgColor();
  const textColor = style?.color || (highlighted ? "#111827" : "#FFFFFF");

  // Thêm hiệu ứng highlight cho node
  const borderWidth = highlighted ? "3px" : "1px";
  const borderColor = highlighted ? "#ef4444" : "rgba(229, 231, 235, 0.5)";
  const boxShadow = highlighted
    ? "0 0 0 2px rgba(239, 68, 68, 0.3), 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
    : "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)";

  // Xác định hình dạng node dựa trên isOval và level
  const getBorderRadius = () => {
    if (style?.borderRadius !== undefined) return `${style.borderRadius}px`;

    // Hình oval cho level cao hoặc isOval=true
    if (isOval || level > 3) return "9999px";

    // Hình dạng khác nhau dựa theo level
    switch (level) {
      case 0:
        return "8px"; // Root node - hình chữ nhật góc tròn
      case 1:
        return "6px"; // Cấp 1 - gần vuông hơn
      case 2:
      case 3:
        return "10px"; // Cấp 2-3 - góc tròn vừa phải
      default:
        return "9999px"; // Cấp cao hơn - hình oval
    }
  };

  // Xác định kích thước node dựa trên level
  const width =
    style?.width ||
    (level === 0 ? 280 : level === 1 ? 240 : level <= 3 ? 220 : 180);
  const fontSize =
    style?.fontSize ||
    (level === 0
      ? "18px"
      : level === 1
        ? "16px"
        : level <= 3
          ? "14px"
          : "12px");
  const fontWeight = style?.fontWeight || (level <= 1 ? "bold" : "normal");
  const nodeOpacity = style?.opacity !== undefined ? style.opacity : 1;

  return (
    <div
      className={`px-4 py-3 transition-all duration-300 cursor-pointer hover:scale-[1.02] hover:shadow-lg`}
      style={{
        backgroundColor: bgColor,
        color: textColor,
        paddingLeft: "20px",
        paddingRight: "20px",
        width: `${width}px`,
        maxWidth: "350px",
        borderWidth,
        borderColor,
        borderStyle: "solid",
        borderRadius: getBorderRadius(),
        boxShadow,
        transition: "all 0.3s ease",
        transform: highlighted ? "scale(1.05)" : "scale(1)",
        zIndex: highlighted ? 10 : 1,
        position: "relative",
        opacity: nodeOpacity,
        fontSize: fontSize,
        fontWeight: fontWeight,
      }}
      data-highlighted={highlighted ? "true" : "false"}
      data-level={level}
    >
      {/* Thêm indicator khi node có thông tin chi tiết */}
      {hasDetails && (
        <div 
          className="absolute -top-2 -right-2 bg-indigo-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-md"
          title="Nhấp để xem chi tiết"
        >
          <Info size={14} />
        </div>
      )}

      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
        className="w-3 h-3 bg-white border-2"
        style={{
          borderColor: highlighted ? "#ef4444" : bgColor,
          zIndex: 20,
          opacity: isConnectable ? 1 : 0,
        }}
      />

      {showImage && image && (
        <div className="mb-3 flex justify-center">
          <img
            src={getImageWithFallback(image, label)}
            alt={label}
            className={`w-12 h-12 object-cover rounded-full border-2 ${highlighted ? "border-red-500" : "border-white"}`}
          />
        </div>
      )}

      <div className="text-center">
        <div className={`${level <= 1 ? "font-semibold" : "font-normal"}`}>
          {label}
        </div>
        {description && level <= 2 && (
          <div className="text-sm mt-2 opacity-90 truncate">{description}</div>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={isConnectable}
        className="w-3 h-3 bg-white border-2"
        style={{
          borderColor: highlighted ? "#ef4444" : bgColor,
          zIndex: 20,
          opacity: isConnectable ? 1 : 0,
          backgroundColor: "white",
          border: `2px solid ${highlighted ? "#ef4444" : bgColor}`,
          boxShadow: "none",
        }}
      />
    </div>
  );
};

export default memo(DiagramNode);
